import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Ayuda } from '../../componentes/Ayuda';
import { Icono } from '../../componentes/Icono';
import logo from '../../estilo/comun/assets/wordmark-cropped.png';
import type { TarjetaAyuda } from '../../marco/cabecera';
import type { SeccionBackoffice } from '../secciones';

/**
 * **Marco del backoffice** (1.20 a 1.22; componentes.md §17, G131): el del prototipo `1.20-1.22-backoffice.html`, con las
 * medidas del panel (0.1) y el aviso de zona interna. Ordenador (≥ 1024): menú lateral de 248 **en rojo** con la franja del
 * logo en `--rv-charcoal` y «Backoffice interno» con el escudo (G120), entradas de 40 con la píldora oscura que se
 * desliza, y la persona abajo (nombre, email, rol y «Cerrar sesión»). Cabecera con el título, su línea y la «?». Móvil:
 * «Backoffice interno» arriba con la «?» y el avatar; barra inferior oscura con el rojo deslizándose, hasta 5 entradas
 * (con más, las 4 primeras y «Más»).
 */
export interface PersonaBackoffice {
    nombre: string;
    email: string;
    iniciales: string;
    /** «Superadmin de plataforma» o «Soporte de plataforma». */
    rol: string;
}

interface Props {
    movil: boolean;
    secciones: readonly SeccionBackoffice[];
    /** Clave de la sección que se ve. */
    actual: string;
    /** Cifra junto a una entrada del menú (prototipo `counts`: la de «Cola de errores»); 0 o sin ella, nada. */
    cifras?: Readonly<Record<string, number>>;
    ir: (clave: string) => void;
    persona: PersonaBackoffice;
    titulo: string;
    subtitulo: string;
    ayuda: readonly TarjetaAyuda[];
    onCerrarSesion: () => void;
    children: ReactNode;
}

export function MarcoBackoffice(p: Props) {
    return p.movil ? <MarcoMovil {...p} /> : <MarcoOrdenador {...p} />;
}

/** La tarjeta de la persona y «Cerrar sesión», que caen escalonadas (prototipo `userCards`). */
function TarjetasPersona({ persona, abierta, arriba, onCerrarSesion }: { persona: PersonaBackoffice; abierta: boolean; arriba: boolean; onCerrarSesion: () => void }) {
    const caida = (i: number, n: number) => ({
        transform: abierta ? 'none' : `translateY(${String((arriba ? 1 : -1) * (16 + i * 56))}px) scale(0.94)`,
        opacity: abierta ? 1 : 0,
        transitionDelay: `${String(abierta ? i * 60 : (n - 1 - i) * 35)}ms`,
    });
    const cabeza = (
        <div key="cabeza" className="rv-bo-tarjeta rv-bo-tarjeta--cabeza" style={caida(arriba ? 1 : 0, 2)}>
            <strong>{persona.nombre}</strong>
            <span>{persona.email}</span>
            <span className="rv-bo-tarjeta__rol">{`${persona.rol} · dos pasos activa`}</span>
        </div>
    );
    const lista = (
        <div key="lista" className="rv-bo-tarjeta rv-bo-tarjeta--lista" style={caida(arriba ? 0 : 1, 2)}>
            <button type="button" className="rv-bo-salir" tabIndex={abierta ? 0 : -1} onClick={onCerrarSesion}>
                <Icono nombre="log-out" />
                <span>Cerrar sesión</span>
            </button>
        </div>
    );
    return <>{[cabeza, lista]}</>;
}

/** Cierra lo abierto con Esc o al tocar fuera. */
function useCerrarFuera(abierto: boolean, cerrar: () => void, caja: RefObject<HTMLElement | null>) {
    useEffect(() => {
        if (!abierto) {
            return;
        }
        const fuera = (e: PointerEvent) => {
            if (caja.current !== null && !caja.current.contains(e.target as Node)) {
                cerrar();
            }
        };
        const tecla = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                cerrar();
            }
        };
        document.addEventListener('pointerdown', fuera);
        document.addEventListener('keydown', tecla);
        return () => {
            document.removeEventListener('pointerdown', fuera);
            document.removeEventListener('keydown', tecla);
        };
    }, [abierto, cerrar, caja]);
}

function MarcoOrdenador({ secciones, actual, cifras, ir, persona, titulo, subtitulo, ayuda, onCerrarSesion, children }: Props) {
    const [conPersona, setConPersona] = useState(false);
    const caja = useRef<HTMLDivElement>(null);
    useCerrarFuera(conPersona, () => { setConPersona(false); }, caja);
    const indice = secciones.findIndex((s) => s.clave === actual);
    return (
        <div className="rv-bo">
            <aside className="rv-bo-lateral" style={{ zIndex: conPersona ? 30 : 10 }}>
                <div className="rv-bo-lateral__logo">
                    <img src={logo} alt="Reservaria" />
                    <span>
                        <Icono nombre="shield" />
                        Backoffice interno
                    </span>
                </div>
                <nav aria-label="Menú del backoffice" className="rv-bo-menu rv-noscroll">
                    <div className="rv-bo-menu__lista">
                        <span className="rv-bo-menu__pildora" aria-hidden="true" style={{ transform: `translateY(${String(Math.max(0, indice) * 42)}px)`, opacity: indice >= 0 ? 1 : 0 }} />
                        {secciones.map((s) => (
                            <button
                                key={s.clave}
                                type="button"
                                className="rv-bo-menu__op"
                                aria-current={s.clave === actual ? 'page' : undefined}
                                onClick={() => {
                                    ir(s.clave);
                                }}
                            >
                                <Icono nombre={s.icono} />
                                <span>{s.etiqueta}</span>
                                {(cifras?.[s.clave] ?? 0) > 0 && <b className="rv-bo-menu__cifra">{cifras?.[s.clave]}</b>}
                            </button>
                        ))}
                    </div>
                </nav>
                <div ref={caja} className="rv-bo-persona">
                    <div className="rv-bo-persona__tarjetas" style={{ pointerEvents: conPersona ? 'auto' : 'none' }} aria-hidden={!conPersona}>
                        <TarjetasPersona persona={persona} abierta={conPersona} arriba onCerrarSesion={onCerrarSesion} />
                    </div>
                    <button
                        type="button"
                        className="rv-bo-persona__b"
                        aria-label="Tu cuenta"
                        aria-expanded={conPersona}
                        style={{ background: conPersona ? 'var(--rv-charcoal)' : undefined }}
                        onClick={() => {
                            setConPersona((a) => !a);
                        }}
                    >
                        <span className="rv-bo-persona__avatar">{persona.iniciales}</span>
                        <span className="rv-bo-persona__textos">
                            <strong>{persona.nombre}</strong>
                            <span>{persona.rol}</span>
                        </span>
                        <Icono nombre="chevron-up" style={{ transform: conPersona ? 'rotate(180deg)' : 'none' }} />
                    </button>
                </div>
            </aside>
            <main className="rv-bo-main">
                <header className="rv-bo-cab">
                    <div className="rv-bo-cab__titulo">
                        <h1>{titulo}</h1>
                        <div>{subtitulo}</div>
                    </div>
                    <Ayuda tarjetas={ayuda} />
                </header>
                <div className="rv-bo-contenido">{children}</div>
            </main>
        </div>
    );
}

function MarcoMovil({ secciones, actual, cifras, ir, persona, titulo, subtitulo, ayuda, onCerrarSesion, children }: Props) {
    const [conPersona, setConPersona] = useState(false);
    const [mas, setMas] = useState(false);
    const caja = useRef<HTMLDivElement>(null);
    useCerrarFuera(conPersona, () => { setConPersona(false); }, caja);
    const muchas = secciones.length > 5;
    const barra = muchas ? secciones.slice(0, 4) : secciones;
    const enMas = muchas ? secciones.slice(4) : [];
    const columnas = barra.length + (muchas ? 1 : 0);
    const enBarra = barra.findIndex((s) => s.clave === actual);
    const indice = mas ? barra.length : enBarra >= 0 ? enBarra : muchas && enMas.some((s) => s.clave === actual) ? barra.length : -1;
    const ir2 = (clave: string) => {
        setMas(false);
        ir(clave);
    };
    return (
        <div className="rv-bo rv-bo--movil">
            <main className="rv-bo-main">
                <header className="rv-bo-cab">
                    <div className="rv-bo-cab__zona">
                        <Icono nombre="shield" />
                        Backoffice interno
                    </div>
                    <div className="rv-bo-cab__herr">
                        <Ayuda tarjetas={ayuda} />
                        <div ref={caja} className="rv-bo-persona-m">
                            <button
                                type="button"
                                className="rv-bo-persona-m__b"
                                aria-label="Tu cuenta"
                                aria-expanded={conPersona}
                                style={{ boxShadow: `0 0 0 2px ${conPersona ? 'var(--rv-red)' : 'transparent'}` }}
                                onClick={() => {
                                    setConPersona((a) => !a);
                                }}
                            >
                                {persona.iniciales}
                            </button>
                            <div className="rv-bo-persona-m__tarjetas" style={{ pointerEvents: conPersona ? 'auto' : 'none' }} aria-hidden={!conPersona}>
                                <TarjetasPersona persona={persona} abierta={conPersona} arriba={false} onCerrarSesion={onCerrarSesion} />
                            </div>
                        </div>
                    </div>
                    <div className="rv-bo-cab__titulo">
                        <h1>{titulo}</h1>
                        <div>{subtitulo}</div>
                    </div>
                </header>
                <div className="rv-bo-contenido">{children}</div>
                <div className="rv-bo-hueco-abajo" />
            </main>
            {mas && (
                <div
                    aria-hidden="true"
                    className="rv-bo-velo"
                    onClick={() => {
                        setMas(false);
                    }}
                />
            )}
            <nav aria-label="Menú del backoffice" className="rv-bo-barra" style={{ height: mas ? 72 + 46 + Math.ceil(enMas.length / 4) * 80 : 72 }}>
                {mas && (
                    <div className="rv-bo-barra__mas">
                        <div className="rv-bo-barra__mas-cab">
                            <span>Más</span>
                            <button
                                type="button"
                                aria-label="Cerrar"
                                onClick={() => {
                                    setMas(false);
                                }}
                            >
                                <Icono nombre="x" />
                            </button>
                        </div>
                        <div className="rv-bo-barra__rejilla">
                            {enMas.map((s) => (
                                <button
                                    key={s.clave}
                                    type="button"
                                    aria-current={s.clave === actual ? 'page' : undefined}
                                    style={{ background: s.clave === actual ? 'var(--rv-red)' : 'var(--rv-graphite)' }}
                                    onClick={() => {
                                        ir2(s.clave);
                                    }}
                                >
                                    <Icono nombre={s.icono} />
                                    <span>{s.corta}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <div className="rv-bo-barra__fila" style={{ gridTemplateColumns: `repeat(${String(columnas)}, minmax(0, 1fr))` }}>
                    <span className="rv-bo-barra__pildora" aria-hidden="true" style={{ width: `calc((100% - 16px) / ${String(columnas)})`, transform: `translateX(${String(Math.max(0, indice) * 100)}%)`, opacity: indice < 0 ? 0 : 1 }} />
                    {barra.map((s, i) => (
                        <button
                            key={s.clave}
                            type="button"
                            aria-current={i === indice ? 'page' : undefined}
                            onClick={() => {
                                ir2(s.clave);
                            }}
                        >
                            <span className="rv-bo-barra__icono">
                                <Icono nombre={s.icono} />
                                {(cifras?.[s.clave] ?? 0) > 0 && <b className="rv-bo-barra__cifra">{cifras?.[s.clave]}</b>}
                            </span>
                            <span>{s.corta}</span>
                        </button>
                    ))}
                    {muchas && (
                        <button
                            type="button"
                            aria-expanded={mas}
                            aria-current={indice === barra.length ? 'page' : undefined}
                            onClick={() => {
                                setMas((m) => !m);
                            }}
                        >
                            <Icono nombre="ellipsis" />
                            <span>Más</span>
                        </button>
                    )}
                </div>
            </nav>
        </div>
    );
}
